// ═══════════════════════════════════════════════════════════════════════════
// episodeScript.js — pull a readable narration script (and a short preview
// snippet) out of a generated episode document. Shared by the inline
// EpisodeAudioPlayer and the workshop feed's play/export + text preview, so
// the narration and the preview always read from the same source of truth.
// ═══════════════════════════════════════════════════════════════════════════

export function stripTags(s) {
  // Inline meta-tags like [PAUSE] / [HOOK] would be read aloud by TTS;
  // strip them so the narration sounds like a script, not directions.
  return String(s || '').replace(/\[[^\]]+\]/g, ' ').replace(/\s+/g, ' ').trim();
}

/* Pulls a single readable script from an episode document, in the order the
   user actually wants to hear it. Handles both doc shapes:
   - architectEpisode raw:  { title, summary, beats[] }
   - canonical (editor):    { title, hook, script, questions[], talking_points[] }
   Beat labels and markdown `## headings` are skipped so the narration sounds
   like a podcast read, not a table of contents. Capped at 4000 chars (the API
   ceiling). */
export function extractEpisodeScript(doc) {
  if (!doc) return '';
  const parts = [];
  if (doc.title) parts.push(stripTags(doc.title));

  // Canonical-shape path: { hook, script | talking_points | questions }
  if (typeof doc.script === 'string' || doc.hook || Array.isArray(doc.talking_points) || Array.isArray(doc.questions)) {
    if (doc.hook) parts.push(stripTags(doc.hook));
    if (typeof doc.script === 'string' && doc.script.trim()) {
      const cleaned = doc.script
        .split(/\n+/)
        .map(line => line.replace(/^#{1,6}\s+/, '').trim())
        .filter(Boolean)
        .join('\n');
      parts.push(stripTags(cleaned));
    } else if (Array.isArray(doc.talking_points) && doc.talking_points.length) {
      parts.push(doc.talking_points.map(p => '• ' + stripTags(p)).join('\n'));
    } else if (Array.isArray(doc.questions) && doc.questions.length) {
      parts.push(doc.questions.map((q, i) => `${i + 1}. ${stripTags(q)}`).join('\n'));
    }
    return parts.filter(Boolean).join('\n\n').slice(0, 4000);
  }

  // architectEpisode-shape path: beats[]
  if (doc.summary) parts.push(stripTags(doc.summary));
  if (Array.isArray(doc.beats)) {
    for (const beat of doc.beats) {
      const body = stripTags(beat?.content || beat?.text || beat?.body || '');
      if (body) parts.push(body);
    }
  }
  return parts.filter(Boolean).join('\n\n').slice(0, 4000);
}

/* True when the episode has enough written content to narrate. The home
   collections are playlists (every card has a play button), so we only surface
   episodes that will actually produce audio — otherwise pressing play throws
   "Nothing to narrate yet." */
export function hasNarratableScript(doc) {
  return !!extractEpisodeScript(doc);
}

/* A short, one-line preview of the episode's actual writing — hook first,
   then summary, then the opening of the script. Used under each workshop
   item so a row shows a taste of the copy, not just the title. Returns ''
   when there's nothing readable yet. */
export function episodePreviewText(doc, max = 160) {
  if (!doc) return '';
  const source =
    stripTags(doc.hook) ||
    stripTags(doc.summary) ||
    (typeof doc.script === 'string'
      ? stripTags(doc.script.replace(/^#{1,6}\s+.*$/gm, ''))
      : '') ||
    (Array.isArray(doc.beats)
      ? stripTags(doc.beats.find(b => b?.content || b?.text || b?.body)?.content
          || doc.beats[0]?.text || doc.beats[0]?.body || '')
      : '') ||
    (Array.isArray(doc.talking_points) ? stripTags(doc.talking_points[0]) : '');
  if (!source) return '';
  return source.length > max ? source.slice(0, max).trimEnd() + '…' : source;
}
